import * as RadixSelect from '@radix-ui/react-select';
import { ReactNode, forwardRef, useState } from 'react';
import { tv } from 'tailwind-variants';

import { buttons } from '@/styles/buttons';
import { popover } from '@/styles/popover';

import { CheckmarkIcon } from '@/ui/Icons/CheckmarkIcon';
import { ChevronDownSmallIcon } from '@/ui/Icons/ChevronDownSmallIcon';

import { classNames } from '@/utils/classnames';

const selectTriggerVariants = tv({
  base: ['flex flex-row gap-2'],
  variants: {
    variant: {
      primary: buttons({
        size: 'small',
        shape: 'pill',
        color: 'purple',
        class: 'pr-3 pl-4',
      }),
      secondary: buttons({
        size: 'small',
        shape: 'rounded',
        color: 'gray',
      }),
      secondaryPill: buttons({
        size: 'small',
        shape: 'pill',
        color: 'gray',
      }),
    },
  },
});

type Item = {
  /**
   * The label to be displayed
   */
  label: ReactNode;
  /**
   * The underlying value of the item
   */
  value: string;
};

type SelectProps = {
  ariaLabel?: string;
  variant: 'primary' | 'secondary' | 'secondaryPill';
  items: readonly Item[];
  placeholderText: string;
  /**
   * The item that should be selected by default.
   * This value will be used to initialize the component.
   * The component will then manage its own state.
   */
  defaultSelected?: string;
  /**
   * The item that should be selected.
   * This value will be used instead of the component's internal state.
   * Use this prop if you want to control the state of the component.
   */
  selected?: string;
  /**
   * Callback that is called when the selected items change
   *
   * @param selected - The currently selected item represented by its value
   */
  onSelect?: (selected: string) => void;
  disabled?: boolean;

  /**
   * Whether the popover should be displayed or not
   * If this parameter is set, the open state of the component becomes controlled.
   */
  open?: boolean;

  /**
   * Sets the default open state of the component.
   * The value of this component will be used to initialize the component but the component
   * will stay uncontrolled.
   */
  defaultOpen?: boolean;

  /**
   * Callback function that will be called when the open state of the component changes
   */
  onOpenChange?: (newState: boolean) => void;
  className?: string;
  /**
   * Text shown in the trigger instead of the selected item's label.
   */
  valueText?: ReactNode;
  /**
   * Additional classes for the dropdown content panel.
   */
  contentClassName?: string;
  /**
   * How the dropdown should be aligned.
   */
  align?: 'start' | 'end' | 'center';
};
export const Select = forwardRef<HTMLButtonElement, SelectProps>(function Select(
  {
    ariaLabel,
    variant = 'primary',
    items,
    placeholderText,
    defaultSelected,
    selected,
    onSelect,
    open,
    defaultOpen,
    onOpenChange,
    disabled = false,
    className,
    valueText,
    contentClassName,
    align = 'end',
  },
  ref,
) {
  const popoverStyles = popover();
  const [internalSelected, setInternalSelect] = useState(defaultSelected);
  const [isOpen, setIsOpen] = useState(defaultOpen === undefined ? false : defaultOpen);

  const valueChangeHandler: (value: string) => void = (value) => {
    if (onSelect) {
      onSelect(value);
    }
    if (selected === undefined) {
      setInternalSelect(value);
    }
  };

  const openChangeHandler: (state: boolean) => void = (state) => {
    if (onOpenChange) {
      onOpenChange(state);
    }
    if (open === undefined) {
      setIsOpen(state);
    }
  };

  return (
    <RadixSelect.Root
      open={open === undefined ? isOpen : open}
      onOpenChange={openChangeHandler}
      value={selected === undefined ? internalSelected : selected}
      onValueChange={valueChangeHandler}
    >
      {/** we need to override the pointerDown handler because of a bug with radix-ui
https://github.com/radix-ui/primitives/issues/1641
        */}
      <RadixSelect.Trigger
        aria-label={ariaLabel}
        onPointerDown={(e) => e.preventDefault()}
        onClick={() => openChangeHandler(!(open ?? isOpen))}
        disabled={disabled}
        ref={ref}
        className={selectTriggerVariants({ variant, class: className })}
      >
        <RadixSelect.Value placeholder={placeholderText}>{valueText}</RadixSelect.Value>
        <RadixSelect.Icon>
          <ChevronDownSmallIcon />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>
      <RadixSelect.Portal>
        <RadixSelect.Content
          className={classNames(
            popoverStyles.base(),
            'max-h-[200px] tablet:max-h-fit',
            contentClassName,
          )}
          align={align}
          position="popper"
        >
          <RadixSelect.ScrollUpButton className="flex justify-center items-center text-token-gray-1600">
            <ChevronDownSmallIcon className="rotate-180" />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport className={popoverStyles.items()}>
            {items.map(({ label, value }) => {
              return (
                <RadixSelect.Item
                  key={value}
                  value={value}
                  className={classNames(popoverStyles.item(), 'data-[state=checked]:font-medium')}
                >
                  <RadixSelect.ItemText>{label}</RadixSelect.ItemText>
                  <RadixSelect.ItemIndicator className="ml-auto flex items-center">
                    <CheckmarkIcon />
                  </RadixSelect.ItemIndicator>
                </RadixSelect.Item>
              );
            })}
          </RadixSelect.Viewport>
          <RadixSelect.ScrollDownButton className="text-token-gray-1600 flex justify-center items-center">
            <ChevronDownSmallIcon />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
});
